import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton, IgDialog, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceListVariationDto,
  GenericExperienceFe,
} from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useEndExperience } from "@src/api/experience-actions/end-experience";
import { usePauseExperience } from "@src/api/experience-actions/pause-experience";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { RolloutAnalyticsTable } from "@src/features/analytics/tables/RolloutsAnalyticsTable";
import { selectedDashboardFilters } from "@src/store/slices/dashboardFilters";
import { getDialog, isDialogOpen, setDialog } from "@src/store/slices/global";
import { endPriceTest } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

import { PriceRolloutWarning } from "./PriceRolloutWarning";

interface PriceUpdaterDialogProps {
  currExperience: GenericExperienceFe;
  variations: (Omit<ExperienceListVariationDto, "experienceId"> & {
    experienceId?: string;
  })[];
  noRedirect?: boolean;
}

export const PriceUpdaterDialog = ({
  currExperience,
  noRedirect,
  variations,
}: PriceUpdaterDialogProps) => {
  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();
  const theme = useTheme();
  const dashboardFilters = useSelector(selectedDashboardFilters);
  const priceUpdaterDialogProps = useSelector(getDialog("priceUpdater"));
  const downloadPriceCSVEnabled = useConfiguration("enable_price_csv_download");
  const endExperience = useEndExperience(noRedirect, currExperience.id);
  const pauseExperience = usePauseExperience();
  const isOpen = useSelector(isDialogOpen("priceUpdater"));
  const handleClose = () => {
    dispatch(setDialog({ dialog: "priceUpdater", open: false, props: "none" }));
  };
  const [selectedVariationId, setSelectedVariationId] = useImmer<string>(
    variations.find((variation) => variation.isControl)?.id || variations[0].id
  );

  const handleExperienceTestAction = (updatePrices: boolean) => {
    if (!priceUpdaterDialogProps.props) {
      return;
    }
    if (priceUpdaterDialogProps.props.action === "pause") {
      pauseExperience.mutate({
        body: {
          variationId: selectedVariationId,
          updatePricesToVariation: updatePrices,
        },
        pathParams: {
          name: currExperience.name,
          experienceId: currExperience.id!,
          action: "pause",
        },
        queryParams: undefined,
      });
    } else if (priceUpdaterDialogProps.props.action === "end") {
      endExperience.mutate({
        body: {
          variationId: selectedVariationId,
          updatePricesToVariation: updatePrices,
        },
        pathParams: {
          name: currExperience.name,
          experienceId: currExperience.id!,
          action: "end",
        },
        queryParams: undefined,
      });
    }

    dispatch(setDialog({ dialog: "priceUpdater", open: false, props: "none" }));
  };

  if (!priceUpdaterDialogProps.props) {
    return null;
  }

  return (
    <IgDialog
      title={
        priceUpdaterDialogProps.props.action === "end"
          ? "End Price Test"
          : "Pause Price Test"
      }
      showCloseIcon={true}
      rawContent={
        <Grid
          container
          direction={"column"}
          textAlign={"center"}
          rowSpacing={2}
        >
          <Grid item marginBottom={"20px"}>
            <IgTypography sx={{ color: theme.palette.text.secondary }}>
              Which group's prices should be applied to Shopify?{" "}
              <IgTypography
                onClick={() => {
                  openNewTab(endPriceTest, "End Price Test");
                }}
                igStyle={"link"}
                sx={{ fontWeight: 400 }}
              >
                Find out more.
              </IgTypography>
            </IgTypography>
          </Grid>

          <RolloutAnalyticsTable
            experience={currExperience}
            variations={variations}
            selectedVariationId={selectedVariationId}
            setSelectedVariationId={setSelectedVariationId}
          />

          {Object.values(currExperience.testTypes).filter(
            (value) => value === true
          ).length > 1 && (
            <Grid item>
              <PriceRolloutWarning experience={currExperience} />
            </Grid>
          )}

          <Grid
            container
            item
            direction={"row"}
            justifyContent={"space-between"}
            marginTop={"10px"}
          >
            {isAdmin || downloadPriceCSVEnabled?.value ? (
              <Grid item>
                <IgButton
                  loading={pauseExperience.isPending || endExperience.isPending}
                  onClick={() => {
                    handleExperienceTestAction(false);
                  }}
                >
                  {priceUpdaterDialogProps.props.action === "end"
                    ? "End"
                    : "Pause"}{" "}
                  Test and Don't Update Prices
                </IgButton>
              </Grid>
            ) : null}
            <Grid item>
              <IgButton
                loading={pauseExperience.isPending || endExperience.isPending}
                variant={"contained"}
                disabled={!selectedVariationId}
                onClick={() => {
                  handleExperienceTestAction(true);
                }}
              >
                {`Apply Prices and ${
                  priceUpdaterDialogProps.props.action === "pause"
                    ? "Pause"
                    : "End"
                } Test`}
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
      }
      handleClose={handleClose}
      showDialog={isOpen}
      dialogHeight={null}
      hideActions={true}
      fullWidth={true}
      noButtonText={"Cancel"}
      maxWidth={"lg"}
    />
  );
};
